/**
 * What the campaign card says when it manages rather than reads (A7, M2): the words on
 * Edit, Delete, Remove and Add ad, where a runner can reach them. The card renders these
 * and decides nothing; the decisions are in `campaign-manage.ts`.
 *
 * 🔴 **Three boundaries are this console's and are said as such.** The funding
 * instrument and a line item's objective, product and placements are «fixed after
 * creation» because the reference's PUT has no such parameters — not because X was seen
 * to refuse them. The 409 on deleting an ACTIVE object is the console's rule — the
 * reference deletes in any state. Nothing here says what X does to a deleted campaign's
 * line items: the reference says nothing, the worker lists what X answered, and the
 * card shows that list as a reading.
 *
 * 🔴 **Every delete sentence carries the object's status and the reference's own words**
 * («not reversible», «We do not cascade deletes»), because the confirmation is the one
 * place an operator reads before the act that cannot be undone.
 */
import type { Campaign, LineItem } from "./api.ts";
import type {
	CampaignEditProblem,
	DeleteOutcome,
	LineItemEditProblem,
	UpdateOutcome,
} from "./campaign-manage.ts";

// ── Buttons and headings ──────────────────────────────────────────────

export const EDIT = "Edit";
export const SAVE = "Save";
export const CANCEL = "Cancel";
export const DELETE_CAMPAIGN = "Delete campaign";
export const DELETE_LINE_ITEM = "Delete line item";
export const REMOVE_PLACE = "Remove";
export const ADD_AD = "Add ad";
export const CONFIRM_DELETE = "Delete it";
export const KEEP = "Keep it";

/**
 * The card's line for a reader who cannot write to this account — about the campaign, not
 * borrowed from the line-item panel (spec 2026-10-23). Who can give the access is the
 * kit's rule as the code holds it, not a guess. Both grant routes ask for one role, a
 * platform admin — the role on the access_member row: `PUT /access/members/:userId/grants`
 * needs it at admin (the Access page), and `PUT /orgs/:orgId/grants` needs isPlatformAdmin,
 * which reads that same row (the Teams page); both pages sit under isPlatform and admin.
 * `canWriteAccount` (campaigns.ts) refuses a team's `member` and a seated `user` whatever
 * they are granted.
 */
export const CAMPAIGN_READ_ONLY =
	"You can read this campaign. Editing, starting, pausing or deleting it writes to X on this ad account and needs write access to the account. A platform admin grants it to a person on the Access page, or lends the account to a team on the Teams page. A team's member role and the user role cannot write here, whatever is granted.";

/**
 * Under the card's heading, beside the reading sentence: what writes from here. 🔴 Until PR 3
 * of the one-screen layout (2026-10-29) the last sentence was «Start, Stop and Promote are on
 * the Line items panel.» — true while they sat under the row's Targeting and Creative buttons;
 * they are on the line item's own screen now, opened by its row's Open.
 */
export const WRITES_HERE =
	"Edit, Remove, Add ad and Delete on this card write to X, on this account. After each one the card reads the object again and shows what X holds — never what was typed. A line item's Start, Pause and Promote are on its own screen in the Line items panel: Open on its row there.";

// ── The campaign's edit ───────────────────────────────────────────────

export const CAMPAIGN_EDIT_HELP =
	"Name and the two budgets, in the instrument's currency. Only a field you change is sent; a field left as it is, or left empty, is left alone — v1 has no way to clear a budget or a name.";

export const CAMPAIGN_NAME_HELP =
	"The reference: «The name for the campaign. Maximum length: 255 characters.» The length is X's to refuse.";

/**
 * The reference's note on the daily budget, carried as a hint and not as a check: the
 * worker sends both figures as typed and X arbitrates (spec rev. 2 §4).
 */
export const BUDGET_HINT =
	"The reference on the daily budget: «This should be less than or equal to the total_budget_amount_local_micro» This console does not check it — X does, and its refusal is shown as X said it.";

/**
 * In place of the campaign's daily field under budget_optimization LINE_ITEM
 * (`campaignDailyOffered`). X's refusal was on the create; the sentence says so.
 */
export const CAMPAIGN_DAILY_ON_AD_GROUPS =
	"No daily budget on this campaign: its budget is on each ad group (budget optimization LINE_ITEM). On the create X refused a campaign daily budget beside it — «Please remove your daily campaign budget before turning on campaign budget optimization» (request 6e24d969badff983, 2026-10-23) — so this form does not offer one. Each ad group's daily budget is on its own Edit.";

/** Beside «Paid by», in place of a field. */
export const INSTRUMENT_FIXED =
	"Fixed after creation: the reference lists no funding_instrument_id among the parameters of PUT campaigns (read 2026-10-21). A campaign paid by another instrument is a new campaign.";

// ── The line item's edit ──────────────────────────────────────────────

export const LINE_ITEM_EDIT_HELP =
	"Name, start, end and total budget. Only a field you change is sent; a time left empty is left alone. The fields hold minutes, so a change under a minute cannot be made from this screen, and a time X holds to the second is left as it is unless the minute changes. Start and Pause are not here — the worker refuses a status beside a field, and the switch has its own buttons on the line item's own screen in the Line items panel.";

export const LINE_ITEM_NAME_HELP = "The reference: «The name for the line item.»";

/** Beside the objective, the product and the placements, in place of fields. */
export const SHAPE_FIXED =
	"Fixed after creation: objective, product type and placements are not among the reference's parameters for PUT line_items (read 2026-10-21). A different objective is a new line item.";

/**
 * The guide on a line item's total, beside the field. Said, not checked: it describes what
 * a wrong figure may do, and the worker does not turn it into a refusal X does not document.
 */
export const LINE_ITEM_TOTAL_CAUTION =
	"X's guide: «The total_budget_amount_local_micro for a line item cannot exceed the total budget for its parent campaign… Setting these values incorrectly may put the overall campaign into a paused and unservable state.» The console does not check it; what X saves is read back after the save and shown.";

/**
 * The daily budget, read only, under a campaign X does not report as LINE_ITEM. Since
 * 2026-10-23 the field is written under a LINE_ITEM campaign (`dailyEditable`); every
 * campaign this console made before then came back CAMPAIGN.
 */
export const LINE_ITEM_DAILY_READ_ONLY =
	"The daily budget is read here and not written under this campaign: X makes it «Only available when budget_optimization is set to LINE_ITEM for the parent campaign», and X does not report this campaign as LINE_ITEM. The wizard asks X for LINE_ITEM when the budget is on each ad group, and reads back what X saved.";

/** Beside the daily field, under a LINE_ITEM campaign. */
export const LINE_ITEM_DAILY_HELP =
	"The reference: «This should be less than or equal to the total_budget_amount_local_micro». The console does not check it; what X saves is read back after the save and shown.";

/** The reference's own caution about the dates, carried as the reference's words. */
export const FLIGHT_CAUTION =
	"The reference, on a line item's figures: «Setting these values incorrectly may put the overall campaign into a paused and unservable state» What X does with a new flight is read back after the save and shown.";

export function campaignEditProblemText(p: CampaignEditProblem): string {
	switch (p) {
		case "name":
			return "A campaign name cannot be blank. Leave it as it was to keep it.";
		case "daily-invalid":
		case "total-invalid":
			return "An amount, 0 or more, with up to two decimals — 10 or 10.50. Nothing else is sent.";
		case "nothing-changed":
			return "Nothing changed, so nothing is sent.";
	}
}

export function lineItemEditProblemText(p: LineItemEditProblem): string {
	switch (p) {
		case "name":
			return "A line item name cannot be blank. Leave it as it was to keep it.";
		case "start":
			return "Start is not a time.";
		case "end":
			return "End is not a time.";
		case "end-before-start":
			return "End must come after Start — with X's own value standing in for a field left empty.";
		case "total-invalid":
		case "daily-invalid":
			return "An amount, 0 or more, with up to two decimals — 10 or 10.50. Nothing else is sent.";
		case "nothing-changed":
			return "Nothing changed, so nothing is sent.";
	}
}

// ── After a save ──────────────────────────────────────────────────────

/**
 * The sentence after a PUT. «Saved» is followed by the re-read, which is the card's
 * answer; a mismatch names every field X reports differently, as the worker did; a
 * refusal with `rule: "immutable"` is said as the boundary it is.
 */
export function savedText(outcome: UpdateOutcome): { variant: "success" | "danger"; text: string } {
	switch (outcome.kind) {
		case "saved":
			return {
				variant: "success",
				text: "Saved. What is shown now is what X reports after the save, read again by id.",
			};
		case "mismatch":
			return {
				variant: "danger",
				text: `X accepted the save and reports something else — ${outcome.mismatch
					.map((m) =>
						"read" in m
							? `${m.field}: sent ${JSON.stringify(m.sent)}, X reports ${JSON.stringify(m.read)}`
							: `${m.field}: sent ${JSON.stringify(m.sent)}, X's answer does not carry the field`,
					)
					.join("; ")}. No second attempt was made; what is shown is X's reading.`,
			};
		case "refused":
			return {
				variant: "danger",
				text:
					outcome.rule === "immutable"
						? `Not changed — this field is fixed after creation (not in the reference's PUT parameters). ${outcome.error}`
						: `Not changed: ${outcome.error}`,
			};
	}
}

// ── Deleting ──────────────────────────────────────────────────────────

/** The reference's own words, quoted where the confirmation is read. */
export const NOT_REVERSIBLE = {
	campaign:
		"The reference: «Deleting a campaign is not reversible and subsequent attempts to delete the resource will return HTTP 404.»",
	lineItem:
		"The reference: «Deleting a line item is not reversible and subsequent attempts to delete the resource will return HTTP 404.»",
	noCascade:
		"The reference: «We do not cascade deletes.» Its promoted tweets are not deleted by X — they stay, with deleted: false — and this console deletes none of them; each has its own Remove in the line item's Ads panel.",
};

/**
 * The console's rule, said as the console's: the worker answers 409 for an ACTIVE object.
 * 🔴 Until PR 3 (2026-10-29) it said «Stop it first, on the Line items panel» — for a line
 * item a place that moved, and for a campaign never the place: a campaign's switch is on its
 * row in the list. Both places are named now, each with its button's word (B2).
 */
export const PAUSE_FIRST =
	"This console deletes only a PAUSED campaign or line item — its own rule, not X's: the reference deletes in any state. Pause it first — a campaign with Pause on its row in the list, a line item with Pause on its own screen in the Line items panel — and read what X reports; then delete.";

/**
 * The in-page confirmation for a campaign. It carries the status, the reference's
 * sentence, and the line items this card read under it — by id — with no sentence about
 * what becomes of them: the reference says nothing about a deleted campaign's line items,
 * and after the delete the card shows what X listed, as a reading.
 */
export function deleteCampaignConfirm(
	c: Pick<Campaign, "id" | "name" | "entityStatus">,
	lineItemIds: readonly string[],
): string {
	const under =
		lineItemIds.length === 0
			? "This card read no line items under it."
			: `This card read ${lineItemIds.length} ${lineItemIds.length === 1 ? "line item" : "line items"} under it: ${lineItemIds.join(", ")}. What X does with them is not something the reference says; after the delete the card shows what X lists under this campaign.`;
	return `Delete campaign «${c.name}» (${c.id}), which X reports ${c.entityStatus}? ${NOT_REVERSIBLE.campaign} ${under}`;
}

/**
 * The in-page confirmation for a line item: the status, the reference's two sentences,
 * and the ads this card read on it, by id — they stay, and so does their own Remove.
 */
export function deleteLineItemConfirm(
	item: Pick<LineItem, "id" | "entityStatus">,
	adIds: readonly string[],
): string {
	const ads =
		adIds.length === 0
			? "This card read no promoted tweet on it."
			: `This card read ${adIds.length} ${adIds.length === 1 ? "promoted tweet" : "promoted tweets"} on it: ${adIds.join(", ")}.`;
	return `Delete line item ${item.id}, which X reports ${item.entityStatus}? ${NOT_REVERSIBLE.lineItem} ${ads} ${NOT_REVERSIBLE.noCascade}`;
}

/**
 * Removing a place is the create's danger reversed: the one criterion gone is a line
 * item reaching everyone, by X's documentation. Said with the count of what remains.
 */
export function removePlaceConfirm(
	label: string,
	remaining: number,
	/** Every criterion that stays excludes (NE) — since targeting v2, «criteria stay» is not «narrowed». */
	onlyExclusionsStay = false,
): string {
	const after =
		remaining === 0
			? "It is the only criterion: without it this line item reaches everyone, by X's documentation (a line item with no targeting criteria targets all users worldwide)."
			: `${remaining} other ${remaining === 1 ? "criterion stays" : "criteria stay"}${onlyExclusionsStay ? `, and ${remaining === 1 ? "it excludes" : "each of them excludes"} (NE): none would narrow this line item to a place it targets, and what X does with one whose criteria only exclude, the reference does not say` : ""}.`;
	return `Remove ${label} from this line item's targeting? ${after} A place removed can be added again as a new criterion, with a new id.`;
}

/**
 * The sentence after a DELETE, from the outcome. «Gone» says which of the two shapes X
 * answered — the object with deleted: true, or a 404 — and what X listed afterwards, as
 * a reading. A failed listing after a confirmed delete is still a delete.
 */
export function deletedText(
	outcome: DeleteOutcome,
	what: "campaign" | "line item" | "criterion",
	id: string,
): { variant: "success" | "warning" | "danger"; title: string; body: string | null } {
	switch (outcome.kind) {
		case "deleted": {
			const how =
				outcome.confirmed === "deleted"
					? "X returned it with deleted: true"
					: "X answers 404 for it now";
			const listed =
				what === "campaign"
					? outcome.listed === null
						? null
						: outcome.listed.length === 0
							? "X lists no line item under it now."
							: `X still lists under it: ${outcome.listed.join(", ")} — what X does with them is not something the reference says; read them on the Line items panel.`
					: what === "line item"
						? outcome.listed === null
							? null
							: outcome.listed.length === 0
								? "X lists no promoted tweet on it, deleted ones included."
								: `X still lists its promoted tweets, with deleted: false, as the reference says: ${outcome.listed.join(", ")}. They were not deleted by this console.`
						: null;
			return {
				variant: "success",
				title: `Deleted ${what} ${id} — ${how}.`,
				body:
					[
						listed,
						outcome.listingFailed === null
							? null
							: `The listing afterwards failed: ${outcome.listingFailed}`,
					]
						.filter((s): s is string => s !== null)
						.join(" ") || null,
			};
		}
		case "pause-first":
			// 🔴 A criterion's 409 is about its LINE ITEM (an age or gender under an ACTIVE one,
			// 2026-10-29): «it» would name the criterion, which has no status. The title names the
			// line item by the id and the status the worker read from X (#235), and the body is the
			// worker's own sentence, which says why.
			if (what === "criterion")
				return {
					variant: "warning",
					title:
						outcome.lineItemId === null
							? `Not removed: X reports its line item ${outcome.entityStatus}.`
							: `Not removed: X reports line item ${outcome.lineItemId} ${outcome.entityStatus}.`,
					body: outcome.error,
				};
			return {
				variant: "warning",
				title: `Not deleted: X reports it ${outcome.entityStatus}.`,
				body: PAUSE_FIRST,
			};
		case "unknown":
			return {
				variant: "warning",
				title: `X did not answer the delete of ${what} ${id}.`,
				body: "Asking again is safe: deleting twice cannot create anything, and a repeat is answered from a read — the card reads it again now.",
			};
		case "disagreed":
			return {
				variant: "danger",
				title: `X answered the delete of ${what} ${id} with the object still there.`,
				body: `${outcome.error} No second attempt was made.`,
			};
		case "refused":
			return { variant: "danger", title: `Not deleted: ${outcome.error}`, body: null };
	}
}

// ── Add ad ────────────────────────────────────────────────────────────

/** Why Add ad is offered only under a PAUSED line item: the Creative panel's rule, in its words. */
export const ADD_AD_PAUSED_ONLY =
	"Add ad attaches a promoted tweet, which X creates ACTIVE with no way to switch it off — so this console attaches only while the line item is PAUSED (its rule, decided 2026-10-08), and the worker refuses otherwise with 409.";

export const ADD_AD_HELP =
	"The same step as the wizard's Ad: write the ad (text, name, link, images or a GIF) or pick an existing tweet. Upload, tweet and attach are sent in that order when you press Attach, each awaited, and the run stops at the first step that is not done.";

export const ATTACH_LABEL = "Attach";

/** After a run on the card that stopped: what is in X, and that Attach stays off for this draft. */
export const ADD_AD_STOPPED = {
	title: "Stopped — do not retry, look the ids up.",
	body: (created: readonly { what: string; id: string }[], mayExist: boolean): string => {
		if (created.length === 0 && !mayExist) {
			return "X refused the step marked above; nothing from this press is in X. Fix it and press Attach again.";
		}
		const list = created.map((c) => `${c.what} ${c.id}`).join(", ");
		return `${list ? `In X now: ${list}. ` : ""}${mayExist ? "The step that stopped may also exist: X did not say. " : ""}A second press would create a second one of each, so Attach stays off for this draft. The line item's ads below are read again; a tweet that exists can be attached as an existing tweet.`;
	},
};

export const ADD_AD_DONE = {
	title: "Attached. The promoted tweet is in X now, ACTIVE as X creates it.",
	body: "The line item's ads below are read again. Whether anything serves is the campaign's answer, above; X's review of the creative is beside the row.",
};
